<template>
  <div class="mb-4" style="background-color: var(--color-light-light-gray); padding: 20px">
    <h4 class="h5 fw-bold mb-3">
      {{ $t("project.previewAndReport.cashFlow.title") }}
    </h4>
    <div
      class="chart-container"
      style="
        position: relative;
        height: 350px;
        width: 100%;
        padding-right: 25px;
      "
    >
      <canvas ref="cashFlowChart" class="chart"></canvas>
    </div>
    <div class="mt-0">
      <p class="mb-0" style="font-size: 12px" v-html="$t('project.previewAndReport.cashFlow.description')">
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  watch,
  onBeforeUnmount,
  PropType,
} from "vue";
import type { Project } from "@/types/projectTypes";
import Chart from "chart.js/auto";
import { cashFlowChartData } from "@/utils/calculationUtils";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "CashFlowSection",
  props: {
    projectData: {
      type: Object as PropType<Project | null>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const cashFlowChart = ref<HTMLCanvasElement | null>(null);
    let cashFlowChartInstance: any = null;

    const createCashFlowChart = async () => {
      if (!cashFlowChart.value || !props.projectData) return;

      try {
        // Get canvas context
        const ctx = cashFlowChart.value.getContext("2d");
        if (!ctx) return;

        // Ensure the previous chart is destroyed properly
        if (cashFlowChartInstance) {
          cashFlowChartInstance.destroy();
          cashFlowChartInstance = null;
          // Add a small delay to ensure the chart is fully destroyed
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        // Get cash flow data from calculationUtils
        const cashFlowData = await cashFlowChartData();

        const style = getComputedStyle(cashFlowChart.value);
        const positiveColor =
          style.getPropertyValue("--color-graph-green").trim() || "#36f1b9";
        const negativeColor =
          style.getPropertyValue("--color-graph-pink").trim() || "#f87ce4";

        // Create color array (red for negative, green for positive)
        const backgroundColors = cashFlowData.map((value: number) =>
          value < 0 ? negativeColor : positiveColor
        );

        // Create chart
        cashFlowChartInstance = new Chart(ctx!, {
          type: "bar",
          data: {
            labels: Array.from({ length: 26 }, (_, i) => `${i}`),
            datasets: [
              {
                label: "",
                data: cashFlowData,
                backgroundColor: backgroundColors,
                borderColor: backgroundColors.map((color: string) =>
                  color.replace("0.7", "1")
                ),
                borderWidth: 1,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                right: 25,
                left: 10,
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    let label = context.dataset.label || "";
                    if (label) {
                      label += ": ";
                    }
                    if (context.parsed.y !== null) {
                      label += new Intl.NumberFormat("en-EU", {
                        style: "currency",
                        currency: "EUR",
                      }).format(context.parsed.y);
                    }
                    return label;
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: false,
                title: {
                  display: true,
                  text: t("project.previewAndReport.cashFlow.yAxis"),
                },
              },
              x: {
                title: {
                  display: true,
                  text: t("project.previewAndReport.cashFlow.xAxis"),
                },
                grid: {
                  display: false,
                },
              },
            },
          },
        });
      } catch (error) {
        console.error("Error creating cash flow chart:", error);
      }
    };

    // Watch for changes in project data to update chart
    watch(
      () => [
        props.projectData?.financial_data?.final_price,
        props.projectData?.financial_data?.annual_revenue,
        props.projectData?.financial_data?.installment_duration_months,
        props.projectData?.financial_data?.installment_ratio,
        props.projectData?.financial_data?.include_renew_inverter,
        props.projectData?.financial_data?.payment_type,
      ],
      async () => {
        await createCashFlowChart();
      }
    );

    // Add cleanup in onBeforeUnmount
    onBeforeUnmount(() => {
      if (cashFlowChartInstance) {
        cashFlowChartInstance.destroy();
      }
    });

    // Create chart when component is mounted
    onMounted(async () => {
      await createCashFlowChart();
    });

    return {
      cashFlowChart,
    };
  },
});
</script>
